<script>
  import BaseLink from '../BaseLink/BaseLink.svelte';
  import BaseHeading from '../BaseHeading/BaseHeading.svelte';
  import { SPRITESHEET_URL } from '../../utils/constants.ts';
  import { getPath } from '../../utils/imageKit.ts';

  let {
    theme = 'blue',
    image = undefined,
    headingText = undefined,
    headingLevel = 'h2',
    content,
    link,
    rounded = true,
    usedInAside = false,
    onClick = undefined,
    displayAsActionPromo = true,
    simplified= false,
    dataTestId = 'info-tile',
  } = $props();
  
  const whiteBackground = 'group bg-white active:shadow-active shadow-y-sm pseudo-shadow-tight-hover';
  let bgCssClassMap = $state([]);
  if (!displayAsActionPromo) {
    bgCssClassMap = {
      blue: 'bg-blue-cerulean',
      pink: 'bg-pink',
      gray: 'bg-gray-pale',
      darkblue: 'bg-blue-darkish',
      white: whiteBackground,
    }
  }

  const whiteHover = 'hover-hover:hover:no-underline';
  let hoverCssClassMap = $state([]);
  if (!displayAsActionPromo) {
    hoverCssClassMap = {
      blue: 'hover-hover:hover:bg-blue-darkish active:bg-blue-darkish',
      pink: 'hover-hover:hover:bg-pink-purplish active:bg-pink-purplish',
      gray: 'hover-hover:hover:bg-gray-light-silver active:bg-gray-light-silver',
      darkblue: 'hover-hover:hover:bg-blue-cerulean active:bg-blue-cerulean',
      white: whiteHover,
    };
  }
  
  let textCssClassMap = $state([]);
  if (!displayAsActionPromo) {
    textCssClassMap = {
      blue: 'text-white',
      pink: 'text-white',
      gray: 'text-blue-cerulean',
      darkblue: 'text-white',
      white: 'text-body',
    };
  }

  const buttonCssClassMap = {
    blue: 'bg-blue-cerulean text-white hover-hover:group-hover:bg-blue-darkish active:bg-blue-darkish',
    pink: 'bg-pink text-white hover-hover:group-hover:bg-pink-purplish active:bg-pink-purplish',
    gray: 'bg-gray-pale text-blue-cerulean hover-hover:group-hover:bg-gray-light-silver active:bg-gray-light-silver',
    darkblue: 'bg-blue-darkish text-white hover-hover:group-hover:bg-blue-cerulean active:bg-blue-cerulean',
    white:
      `${displayAsActionPromo ? 'bg-blue-robin-egg hover-hover:group-hover:bg-blue-light group-active:bg-blue-light' : 'bg-blue-bright-cyan hover-hover:group-hover:bg-blue-robin-egg group-active:bg-blue-robin-egg'} text-blue-navy`,
  };

  // Handle Image Setup
  const defaultSrc = getPath(image, { w: 64, ar: '1:1' });
  const highResSrc = getPath(image, { w: 64, ar: '1:1' }, 2);
</script>

<BaseLink
  className={`group h-full w-full min-h-18 flex items-center relative overflow-hidden 
    ${bgCssClassMap.length ? bgCssClassMap[theme] : whiteBackground} 
    ${textCssClassMap.length ? textCssClassMap[theme] : 'text-body'} 
    ${hoverCssClassMap.length ? hoverCssClassMap[theme] : whiteHover}
    ${!displayAsActionPromo ? 'px-2 md:pr-4' : ''} ${rounded || displayAsActionPromo ? 'rounded-lg' : ''}`}
  {...link}
  {dataTestId}
  {onClick}
>
  <div class="flex flex-col w-full h-full">
    <div class="grow flex items-center" class:md:items-start={displayAsActionPromo} class:p-4={displayAsActionPromo}>
      {#if image && image.url && image.alt}
        <img
          class="w-16 h-16"
          loading="lazy"
          width="64"
          height="64"
          src={defaultSrc}
          srcset={`${defaultSrc}, ${highResSrc} 2x`}
          sizes="64px"
          alt={image.alt}
        />
      {/if}
      <div class="w-4/6 grow mx-2 md:mx-4 text-base font-normal" class:py-4={!displayAsActionPromo}>
        {#if headingText}
          <BaseHeading
            className="mb-1 leading-tight font-semibold text-blue-dark-navy text-lg {!usedInAside ? 'lg:text-xl' : ''}"
            {headingLevel}
          >
            {headingText}
          </BaseHeading>
        {/if}
        {@html content}
      </div>
      <svg
        class={`md:hidden w-4 h-4 ml-auto mr-0 fill-blue-cerulean ${textCssClassMap[theme]}`}
        class:hidden={displayAsActionPromo && !simplified}
        role="presentation"
      >
        <use xlink:href="{SPRITESHEET_URL}right-arrow" />
      </svg>

      {#if !displayAsActionPromo}
        <div
          class={`hidden ${usedInAside ? 'md:block lg:hidden' : 'md:block'} ${buttonCssClassMap[theme]} font-semibold rounded-sm whitespace-nowrap py-2 px-4 lg:px-8 ${usedInAside ? 'mr-4 ml-8' : ''}`}
        >
          {link.text}
        </div>
      {/if}
    </div>

    {#if displayAsActionPromo && link}
      <div
        class={`w-full flex justify-between items-center pl-4 pr-3 py-2 order-3 col-span-3 font-semibold
        text-left text-0.5sm sm:text-sm lg:text-0.5sm hover-hover:group-hover:underline ${buttonCssClassMap[theme]}`}
        class:hidden={simplified}
        class:md:flex={simplified}
      >
        {link.text}
        <svg
          class="ml-2 min-w-4 h-4 w-4 fill-current text-current a11y-no-transform block duration-75 transition-transform ease-in
        hover-hover:group-hover:scale-125 group-active:scale-125"
          role="presentation"
        >
          <use xlink:href="{SPRITESHEET_URL}chevron-right" />
        </svg>
      </div>
    {/if}
  </div>  
</BaseLink>
